import {
  InputAdornment,
  Typography,
  TextField,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormikTextField from '../../Common/Formik/FormikTextField';
import FormikTimeTextfield from '../../Common/Formik/FormikTimeTextfield';
import {
  HoursToMilliseconds,
  MinutesToMilliseconds,
  onKeyDown,
  cleanDateString,
} from '../../../Utils/dateUtils';
import Grid from '@mui/material/Unstable_Grid2';
import { useEffect, useState } from 'react';

const TicketAdditionalDetails = ({ classes, formik, eventInfo, disabled }) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    setHasLoaded(true);
  }, []);

  const GetHoursAndMinutesFromDate = (date) => {
    if (date === undefined || date === null) {
      return undefined;
    }
    const time = new Date(date).getTime();
    if (Number.isNaN(time)) {
      return undefined;
    }
    // The value is stored as a pure UTC offset from the epoch
    // (HoursToMilliseconds(hour) + MinutesToMilliseconds(min)), so read it
    // back the same way. Using local-time getters here would add/subtract the
    // timezone/DST offset and shift the displayed hour (e.g. 13:00 -> 14:00
    // during daylight saving time).
    const totalMinutes = Math.round(time / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  };

  return (
    <>
      <Grid className={classes.detailsHeading}>
        <Typography variant="body2">Details (optional)</Typography>
      </Grid>
      <Grid container direction="row" xs={12} spacing={2}>
        <Grid xs={6}>
          <DatePicker
            fullWidth
            disabled={disabled}
            variant="inline"
            label="Race start date"
            name="localStartDate"
            format="PPP"
            inputFormat="dd MMM yyyy"
            minDate={new Date(eventInfo.basicInfo.eventStartDate)}
            maxDate={new Date(eventInfo.basicInfo.eventEndDate)}
            value={cleanDateString(formik.values.localStartDate)}
            error={
              formik.touched.localStartDate &&
              Boolean(formik.errors.localStartDate)
            }
            onChange={(date) => {
              if (date) date = date.toDateString();
              formik.setFieldValue('localStartDate', date, true);
            }}
            onBlur={formik.handleBlur}
            renderInput={(props) => (
              <TextField onKeyDown={onKeyDown} fullWidth {...props} />
            )}
          />
        </Grid>
        <Grid xs={6}>
          <FormikTimeTextfield
            name="localStartTime"
            label="Race start time"
            value={formik.values.localStartTime}
            handleBlur={formik.handleBlur}
            disabled={disabled}
          />
        </Grid>
        {hasLoaded && (
          <Grid xs={6}>
            <FormikTextField
              variant="outlined"
              label="Cut-off time"
              type="text"
              name="cutoffTime-input"
              placeholder="HH:MM"
              fullWidth
              disabled={disabled}
              defaultValue={
                formik.values.cutoffTime &&
                new Date(formik.values.cutoffTime).getTime() > 0
                  ? GetHoursAndMinutesFromDate(formik.values.cutoffTime)
                  : undefined
              }
              onChange={(e) => {
                const inp = e.target.value.replace(/[^0123456789:]/g, '');
                e.target.value = inp;
                if (inp.length === 0) {
                  formik.setFieldValue('cutoffTime', undefined);
                } else if (inp.split(':').length === 1) {
                  formik.setErrors({
                    cutoffTime: 'Please provide minutes',
                  });
                  if (inp.length > 4) {
                    const time = new Date(HoursToMilliseconds(inp.slice(0, 4)));
                    formik.setFieldValue('cutoffTime', time);
                    e.target.value = inp.slice(0, 4);
                  } else {
                    const time = new Date(HoursToMilliseconds(inp));
                    formik.setFieldValue('cutoffTime', time);
                  }
                } else {
                  const [hours, minute] = inp.split(':');
                  const min = Math.min(
                    Number(minute.length > 2 ? minute.substring(1) : minute),
                    59
                  );
                  const hour = hours.slice(0, 4);
                  const time = new Date(
                    HoursToMilliseconds(hour) + MinutesToMilliseconds(min)
                  );
                  formik.setFieldValue('cutoffTime', time);
                  if (
                    hours.length > 4 ||
                    minute.length > 2 ||
                    Number(minute) > 59
                  ) {
                    e.target.value = `${hour}:${String(min).padStart(2, '0')}`;
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value.length > 0) {
                  e.target.value = GetHoursAndMinutesFromDate(
                    formik.values.cutoffTime
                  );
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment>Hours</InputAdornment>,
              }}
            />
            {formik.touched.cutoffTime &&
              formik.errors.cutoffTime &&
              typeof formik.errors.cutoffTime === 'string' && (
                <FormHelperText error={true}>
                  {formik.errors.cutoffTime}
                </FormHelperText>
              )}
          </Grid>
        )}
        <Grid xs={6} />
        <Grid xs={6}>
          <FormikTextField
            variant="outlined"
            label="Min age limit"
            placeholder="No limit"
            fullWidth
            name="ageMin"
            type="number"
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment>Years old</InputAdornment>,
            }}
          />
        </Grid>
        <Grid xs={6}>
          <FormikTextField
            variant="outlined"
            label="Max age limit"
            placeholder="No limit"
            fullWidth
            name="ageMax"
            type="number"
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment>Years old</InputAdornment>,
            }}
          />
        </Grid>
        <Grid xs={6}>
          <FormikTextField
            variant="outlined"
            label="Elevation gain"
            placeholder="Elevation gain"
            fullWidth
            type="number"
            name="elevationGain"
            step={0.1}
            min={0}
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment>Meters</InputAdornment>,
            }}
          />
        </Grid>
        <Grid xs={6}>
          <FormikTextField
            variant="outlined"
            label="Elevation drop"
            placeholder="Elevation drop"
            fullWidth
            type="number"
            name="elevationDrop"
            step={0.1}
            min={0}
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment>Meters</InputAdornment>,
            }}
          />
        </Grid>
        <Grid xs={12}>
          <FormikTextField
            variant="outlined"
            label="Short description about this ticket"
            rows={4}
            multiline
            fullWidth
            name="description"
            disabled={disabled}
            helperText='You can add information here, which is specific to this ticket (for example: what is included in the fee, course details, requirements, etc.)
            This will be displayed in the "More information" section on the ticket together with the race start time and age limit. (Max. 800 characters)'
          />
        </Grid>
        <Grid xs={6} className={classes.detailsMarginTop}>
          <FormikTextField
            variant="outlined"
            label="Ticket limit"
            placeholder="No limit"
            type="number"
            multiline
            fullWidth
            name="ticketLimit"
            disabled={disabled}
            helperText='Once the limit is reached, this ticket will be marked as "Sold Out"'
          />
        </Grid>
      </Grid>
    </>
  );
};
export default TicketAdditionalDetails;
